'use client';

import dayjs from 'dayjs';
import { Check, ChevronLeft, ChevronRight } from 'lucide-react';
import { usePathname, useRouter } from 'next/navigation';
import { useCallback, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { trackEvent } from '@/utils/analytics';
import { usePageViewTracking } from '@/utils/hooks';

interface TripDate {
  id: string;
  startDate: Date;
  endDate: Date;
  price: number;
}

interface BookingSidebarProps {
  items: TripDate[];
  tripSlug: string;
  tripDuration: number;
  basePrice: number;
  allowCustomDates: boolean;
  className?: string;
}

const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

type TabMode = 'scheduled' | 'custom';

export function BookingSidebar({
  items,
  tripSlug,
  tripDuration,
  basePrice,
  allowCustomDates,
  className,
}: BookingSidebarProps) {
  usePageViewTracking();

  const router = useRouter();
  const pathname = usePathname();

  const [tabMode, setTabMode] = useState<TabMode>(
    items.length > 0 ? 'scheduled' : 'custom',
  );
  const [selectedTripItem, setSelectedTripItem] = useState<TripDate | null>(
    items[0] ?? null,
  );
  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(null);

  const today = dayjs();
  const [currentMonth, setCurrentMonth] = useState(today.startOf('month'));

  const getTripItemForDate = useCallback(
    (date: dayjs.Dayjs): TripDate | null =>
      items.find((item) => {
        const start = dayjs(item.startDate).startOf('day');
        const end = dayjs(item.endDate).endOf('day');
        return (
          (date.isSame(start, 'day') || date.isAfter(start, 'day')) &&
          (date.isSame(end, 'day') || date.isBefore(end, 'day'))
        );
      }) ?? null,
    [items],
  );

  const effectivePrice = useMemo(() => {
    if (selectedTripItem) {
      return selectedTripItem.price;
    }
    if (selectedDate) {
      const match = getTripItemForDate(selectedDate);
      if (match) {
        return match.price;
      }
    }
    if (basePrice > 0) {
      return basePrice;
    }
    if (items.length > 0) {
      return Math.min(...items.map((item) => item.price));
    }
    return 0;
  }, [selectedTripItem, selectedDate, basePrice, items, getTripItemForDate]);

  const calendarDays = useMemo(() => {
    const startOfMonth = currentMonth.startOf('month');
    const endOfMonth = currentMonth.endOf('month');
    const startDay = startOfMonth.day();
    const days: (dayjs.Dayjs | null)[] = [];
    for (let i = 0; i < startDay; i++) {
      days.push(null);
    }
    for (let d = 1; d <= endOfMonth.date(); d++) {
      days.push(currentMonth.date(d));
    }
    return days;
  }, [currentMonth]);

  const handleSelectTripItem = (item: TripDate) => {
    setSelectedTripItem(item);
    trackEvent({
      action: 'select_trip_date',
      category: 'bookings',
      label: `${dayjs(item.startDate).format('DD MMM YYYY')} - ${dayjs(item.endDate).format('DD MMM YYYY')}`,
      value: item.price,
    });
  };

  const handleSelectDate = (date: dayjs.Dayjs) => {
    if (date.isBefore(today, 'day')) {
      return;
    }
    setSelectedDate(date);
    setSelectedTripItem(null);
    trackEvent({
      action: 'select_trip_date',
      category: 'bookings',
      label: date.format('DD MMM YYYY'),
      value: getTripItemForDate(date)?.price ?? basePrice,
    });
  };

  const handleBookNow = () => {
    if (allowCustomDates && tabMode === 'custom') {
      if (!selectedDate) {
        toast.error('Please select a date before booking');
        return;
      }
      trackEvent({
        action: 'book_now_click',
        category: 'bookings',
        label: pathname || 'trip page',
        value: effectivePrice,
      });
      router.push(
        `${pathname}/booking?startDate=${selectedDate.toISOString()}&tripSlug=${tripSlug}`,
      );
    } else {
      if (!selectedTripItem) {
        toast.error('Please select a date before booking');
        return;
      }
      trackEvent({
        action: 'book_now_click',
        category: 'bookings',
        label: pathname || 'trip page',
        value: selectedTripItem.price,
      });
      router.push(`${pathname}/booking?tripItemId=${selectedTripItem.id}`);
    }
  };

  const canBook =
    (allowCustomDates && tabMode === 'custom' && selectedDate) ||
    (!allowCustomDates && selectedTripItem) ||
    (allowCustomDates && tabMode === 'scheduled' && selectedTripItem);

  const canGoPrev = currentMonth.isAfter(today.startOf('month'), 'month');

  const isSelected = (date: dayjs.Dayjs) =>
    selectedDate?.isSame(date, 'day') ?? false;
  const isPast = (date: dayjs.Dayjs) => date.isBefore(today, 'day');
  const hasTripItem = (date: dayjs.Dayjs) => getTripItemForDate(date) !== null;

  const scheduledListContent = (
    <div className="max-h-[30vh] overflow-y-auto pr-2 custom-scrollbar">
      <div className="space-y-3">
        {items.map((item) => {
          const isItemSelected = selectedTripItem?.id === item.id;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => handleSelectTripItem(item)}
              className={`w-full text-left border rounded-xl p-4 transition-all ${
                isItemSelected
                  ? 'border-accent bg-accent/10'
                  : 'border-transparent hover:bg-gray-50'
              }`}
            >
              <div className="flex justify-between items-center gap-3">
                <div className="flex items-center gap-2">
                  <div
                    className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                      isItemSelected
                        ? 'bg-accent text-white'
                        : 'border border-gray-300'
                    }`}
                  >
                    {isItemSelected && <Check size={14} />}
                  </div>
                  <span className="font-medium text-sm md:text-base">
                    {dayjs(item.startDate).format('DD MMM YYYY')} -{' '}
                    {dayjs(item.endDate).format('DD MMM YYYY')}
                  </span>
                </div>
                <span className="text-accent font-semibold shrink-0">
                  ${item.price.toLocaleString()}
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );

  const calendarContent = (
    <div className="border border-gray-200 rounded-2xl overflow-hidden">
      <div className="flex items-center justify-between bg-gray-900 text-white px-4 py-3">
        <button
          type="button"
          onClick={() => setCurrentMonth((m) => m.subtract(1, 'month'))}
          disabled={!canGoPrev}
          className="p-1 hover:bg-gray-700 rounded disabled:opacity-30 disabled:cursor-not-allowed"
          aria-label="Previous month"
        >
          <ChevronLeft size={18} />
        </button>
        <span className="font-medium text-sm">
          {currentMonth.format('MMMM YYYY')}
        </span>
        <button
          type="button"
          onClick={() => setCurrentMonth((m) => m.add(1, 'month'))}
          className="p-1 hover:bg-gray-700 rounded"
          aria-label="Next month"
        >
          <ChevronRight size={18} />
        </button>
      </div>
      <div className="grid grid-cols-7 text-center text-xs font-medium text-gray-500 py-2 border-b border-gray-100">
        {DAYS_OF_WEEK.map((day) => (
          <div key={day} className="py-1">
            {day.charAt(0)}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 text-center text-sm">
        {calendarDays.map((date, position) => {
          if (!date) {
            // biome-ignore lint/suspicious/noArrayIndexKey: empty calendar cells have no other stable key
            return <div key={`pad-${position}`} className="py-2" />;
          }
          const past = isPast(date);
          const selected = isSelected(date);
          const hasItem = hasTripItem(date);
          return (
            <button
              key={date.format('YYYY-MM-DD')}
              type="button"
              disabled={past}
              onClick={() => handleSelectDate(date)}
              className={`py-2 relative transition-colors ${
                past
                  ? 'text-gray-300 cursor-not-allowed'
                  : selected
                    ? 'bg-accent text-white font-semibold'
                    : hasItem
                      ? 'text-accent font-semibold hover:bg-accent/10 cursor-pointer'
                      : 'text-gray-700 hover:bg-gray-50 cursor-pointer'
              }`}
            >
              {date.date()}
              {hasItem && !selected && (
                <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 bg-accent rounded-full" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className={className}>
      <div className="bg-white rounded-4xl p-6 shadow-sm">
        <h2 className="text-2xl font-[500] mb-2">Book This Tour</h2>

        {effectivePrice > 0 && (
          <p className="text-sm text-gray-500 mb-4">
            From{' '}
            <span className="text-accent font-semibold text-lg">
              ${effectivePrice.toLocaleString()}
            </span>{' '}
            / person
          </p>
        )}

        <div className="w-full h-1 bg-accent rounded-full mb-4" />

        {allowCustomDates ? (
          <>
            <div className="flex gap-2 rounded-xl bg-gray-100 p-1.5 mb-4">
              <button
                type="button"
                onClick={() => setTabMode('scheduled')}
                className={`flex-1 py-2 text-sm font-medium rounded-lg transition-colors ${
                  tabMode === 'scheduled'
                    ? 'bg-white text-foreground shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Our Scheduled Dates
              </button>
              <button
                type="button"
                onClick={() => setTabMode('custom')}
                className={`flex-1 py-2 text-sm font-medium rounded-lg transition-colors ${
                  tabMode === 'custom'
                    ? 'bg-white text-foreground shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Pick Your Date
              </button>
            </div>

            {tabMode === 'scheduled' ? (
              items.length > 0 ? (
                scheduledListContent
              ) : (
                <p className="text-center py-6 text-gray-500">
                  No scheduled dates. Use &quot;Pick Your Date&quot; to choose any
                  date.
                </p>
              )
            ) : (
              <>
                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-700">From:</p>
                  <div className="mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50">
                    {selectedDate
                      ? selectedDate.format('DD/MM/YYYY')
                      : 'Select a date'}
                  </div>
                </div>
                {tripDuration > 0 && (
                  <div className="mb-4">
                    <p className="text-sm font-medium text-gray-700">Duration:</p>
                    <div className="mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50">
                      {tripDuration} {tripDuration === 1 ? 'Day' : 'Days'}
                    </div>
                  </div>
                )}
                {calendarContent}
              </>
            )}
          </>
        ) : items.length > 0 ? (
          scheduledListContent
        ) : (
          <p className="text-center py-6 text-gray-500">
            No dates available. Contact us for more options.
          </p>
        )}

        <button
          type="button"
          onClick={handleBookNow}
          disabled={!canBook}
          className="w-full mt-6 bg-accent text-accent-foreground py-3 rounded-full font-medium hover:bg-accent/90 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Book this trip now"
        >
          Book Now
        </button>
      </div>
    </div>
  );
}
